import { Injectable, OnDestroy } from '@angular/core';

// third party imports
import { Observable, Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { LatLng, Map as LeafletMap } from 'leaflet';
import { filter, first, takeUntil } from 'rxjs/operators';

// store
import { AppState } from '@app/store';
// store selectors
import {
  selectAllAssets,
  selectAllVehicles,
  selectAssetCount,
  selectAssetParams,
  selectAssetsLoadError,
  selectAssetsLoadState,
  selectChosenAsset,
  selectIsAssetSelected,
  selectNearbyAssets,
  selectNearbyAssetsEnabled,
  selectNearbyAssetsLoadState,
  selectPathSummary,
  selectPathTrips,
  selectRecentPath,
  selectRecentPathLoadState,
  selectRecentPathStartTime,
  selectReverseGeocode,
  selectReverseGeocodeStatus,
  selectSelectedAssetLoadingState,
  selectSelectedPathSegment,
  selectVehiclesLoadState
} from '@app/store/asset/selectors/assets.selectors';
import { selectAllFilters, selectCurrentFilter } from '@app/store/filters/selectors/filters.selectors';
import {
  selectFilterChipLabels,
  selectShowFilterChipsForLimitedUser
} from '@app/store/filters/selectors/filter-chips.selectors';
import {
  selectCompaniesLoadingState,
  selectCompaniesSearchFilterOptions,
  selectDivisionsSearchFilterOptions
  // selectLocationsSearchFilterOptions
} from '@app/store/filters/selectors/search-filters.selector';
import { selectAllPermissions, selectReadCompaniesPerm } from '@app/store/permissions/selectors/permissions.selectors';
import {
  selectViewContext,
  selectViewPaneOpen,
  selectViewSubContext
} from '@app/store/layout/selectors/layout.selectors';

// store actions
import {
  //clearSearchFiltersLocations,
  getCompaniesFailure,
  getSearchFiltersDivisions,
  setLimitedUserCompanyAndDivisions,
  setLocationsForLimitedUser
} from '@app/store/filters/actions/search-filters.actions';
import {
  applyFilters,
  applySorting,
  clearFilters,
  removeFilter,
  setCompany,
  setCurrentCompany,
  setDivisions,
  updateUrlOnFilterChange
  //setLocations
} from '@app/store/filters/actions/filters.actions';
import { loadPermissions } from '@app/store/permissions/actions/permissions.actions';
import { selectPlacesLoadState, selectPlacesResults } from '@app/store/places/selectors/places.selectors';

import {
  clearNearbyAssets,
  clearRecentPath,
  clearRecentPathSelectedSegment,
  loadAssets,
  loadAssetsFailure,
  loadNearbyAssets,
  loadRecentPath,
  loadSelectedAsset,
  setRecentPathSelectedSegment,
  setRecentPathStartTime,
  startAssetsPolling,
  stopAssetsPolling,
  loadSelectedAssetSuccess,
  loadSelectedAssetFailure,
  clearSelectedAsset,
  loadSelectedAssetById,
  loadVehicles,
  forceUpdateAssets
} from '@app/store/asset/actions/assets.actions';
import {
  setViewContext,
  setViewFiltersOpen,
  setViewPaneOpen,
  setViewSubContext
} from '@app/store/layout/actions/layout.actions';
import { clearPlacesResult, loadPlacesResult } from '@app/store/places/actions/places.actions';
import { clearDriverProfile, loadDrivers } from '@app/store/driver/actions/driver.actions';

// services
import { LeafletService } from '@app/modules/location/services/leaflet.service';
import { ClusterLayerConfigService } from '@app/modules/location/services/leaflet-layer-configs/cluster-layer-config.service';
import { PlatformFacade } from '@app/modules/platform/facade/platform.facade';

// models
import { DropdownOption } from '@livemaps/searchable-dropdown';
import { EntityResource, Filter, FiltersState } from '@app/store/filters/models/filters.model';
import { ResourceLoadState } from '@app/store/filters/models/resource-load.state';
import { DetailsSubcontext, ViewContext } from '@app/store/layout/reducers/layout.reducer';
import { ViewableAsset } from '@app/modules/location/models/viewable-asset.model';
import { AssetsParams, Asset } from '@app/modules/location-client/location-api.models';
import { ReverseGeocoderService } from '@app/modules/reverse-geocoder/services/reverse-geocoder.service';
import { AutoSuggestionResponse, AutoSuggestRequest, Position } from '../models/auto-suggestion.model';
import { selectDrivers, selectDriversLoadState } from '@app/store/driver/selectors/driver.selector';
import { DriverProfle } from '@app/modules/driver/models/driver.models';
import { VehicleListItem } from '@app/core/models/zonar-accounts.model';

@Injectable({
  providedIn: 'root'
})
export class LocationFacade implements OnDestroy {
  private onDestroy$ = new Subject();
  viewSubContext: DetailsSubcontext;

  constructor(
    private leafletService: LeafletService,
    private clusterLayerConfigService: ClusterLayerConfigService,
    private platformFacade: PlatformFacade,
    private store: Store<AppState>,
    private geocode: ReverseGeocoderService
  ) {
    this.clusterLayerConfigService.pinClickAsset$
      .pipe(
        filter(asset => Boolean(asset)),
        takeUntil(this.onDestroy$)
      )
      .subscribe(asset => {
        this.setSelectedAsset(asset);
      });

    this.getSelectedViewSubContext()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(c => (this.viewSubContext = c));
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  // ===== Platform Facade =====
  getOrientation() {
    return this.platformFacade.getOrientation();
  }

  getIsMobile() {
    return this.platformFacade.getIsMobile();
  }

  // ===== Leaflet Service =====
  receiveMap($event: LeafletMap) {
    this.leafletService.receiveMap($event);
  }

  setHover($event) {
    this.leafletService.setHighlightMarker($event);
  }

  clearHover() {
    this.leafletService.clearHighlightMarker();
  }

  refreshMap(assets, nearbyAssets, selectedAsset?) {
    this.leafletService.refreshMap(assets, nearbyAssets, selectedAsset);
  }

  zoomToAssets(assets) {
    this.leafletService.zoomToAssets(assets);
  }

  showClusterLayer() {
    this.leafletService.showClusterLayer();
  }

  clearClusterLayer() {
    this.leafletService.clearClusterLayer();
  }

  showSelectedLocation(position: Position) {
    this.leafletService.showSelectedLocation(position);
  }

  getMapCenter(): LatLng {
    return this.leafletService.getMapCenter();
  }

  clearSelectedLocationMarker() {
    this.leafletService.clearSelectedLocationMarker();
  }

  restoreLiveMap() {
    this.leafletService.restoreLiveMap();
  }

  // ===== Store Actions =====
  applyFilters(filterArgs) {
    this.store.dispatch(applyFilters({ filterArgs: filterArgs }));
  }

  updateUrlOnFilterChange(filterArgs) {
    this.store.dispatch(updateUrlOnFilterChange({ filterArgs: filterArgs }));
  }

  applySorting(sorting) {
    this.store.dispatch(applySorting({ sorting }));
  }

  clearFilters() {
    this.store.dispatch(clearFilters());
  }

  loadPermissions() {
    this.store.dispatch(loadPermissions());
  }

  removeFilter(name) {
    this.store.dispatch(removeFilter({ filter: name }));
  }

  startAssetsPolling() {
    this.store.dispatch(startAssetsPolling());
  }

  setCompanyFilter(company: string): void {
    this.store.dispatch(setCompany({ company }));
  }

  setDivisionFilter(divisions: string[]): void {
    this.store.dispatch(setDivisions({ divisions }));
  }

  loadAccountDrivers(accountId: string): void {
    this.store.dispatch(loadDrivers({ accountId }));
  }

  getAllDrivers(): Observable<Map<string, DriverProfle>> {
    return this.store.select(selectDrivers);
  }

  getAllDriversLoadState(): Observable<ResourceLoadState> {
    return this.store.select(selectDriversLoadState);
  }

  // setLocationFilter(locations: string[]): void {
  //   this.store.dispatch(setLocations({ locations }));
  // }

  getDivisionsByCompanyId(companyId: string): void {
    this.store.dispatch(getSearchFiltersDivisions({ companyId }));
  }

  stopAssetsPolling(): void {
    this.store.dispatch(stopAssetsPolling());
  }

  setSelectedAsset(asset: ViewableAsset): void {
    this.store.dispatch(clearDriverProfile());
    this.store.dispatch(clearRecentPath());
    this.store.dispatch(loadSelectedAsset({ selectedAsset: asset }));
  }

  setViewContext(context: ViewContext) {
    this.store.dispatch(setViewContext({ context }));
  }

  setViewSubContext(subContext: DetailsSubcontext) {
    this.store.dispatch(setViewSubContext({ subContext }));
    if (subContext === DetailsSubcontext.LIVE) {
      this.store
        .select(selectChosenAsset)
        .pipe(first())
        .subscribe(selectedAsset => {
          if (selectedAsset) {
            this.setSelectedAsset(selectedAsset);
          }
        });
    }
  }

  setViewFiltersOpen(filtersOpen: boolean) {
    this.store.dispatch(setViewFiltersOpen({ filtersOpen }));
  }

  setViewPaneOpen(paneOpen: boolean) {
    this.store.dispatch(setViewPaneOpen({ paneOpen }));
  }

  fetchNearbyAssets(selectedAsset: ViewableAsset) {
    this.store.dispatch(loadNearbyAssets({ selectedAsset }));
  }

  clearNearbyAssets() {
    this.store.dispatch(clearNearbyAssets());
  }

  getRecentPath() {
    return this.store.select(selectRecentPath);
  }

  setRecentPath(selectedAsset: ViewableAsset) {
    this.store.dispatch(loadRecentPath({ selectedAsset }));
  }

  clearRecentPath() {
    this.store.dispatch(clearRecentPath());
  }

  clearRecentPathSelectedSegment() {
    this.store.dispatch(clearRecentPathSelectedSegment());
  }

  setRecentPathStartTime(startTime: Date) {
    this.store.dispatch(setRecentPathStartTime({ startTime }));

    this.store
      .select(selectChosenAsset)
      .pipe(first())
      .subscribe(selectedAsset => {
        if (selectedAsset) {
          this.setRecentPath(selectedAsset);
        }
      });
  }

  getRecentPathLoadState() {
    return this.store.select(selectRecentPathLoadState);
  }

  getRecentPathStartTime() {
    return this.store.select(selectRecentPathStartTime);
  }

  getRecentPathTrips() {
    return this.store.select(selectPathTrips);
  }

  getRecentPathSummary() {
    return this.store.select(selectPathSummary);
  }

  getRecentPathSelectedSegment() {
    return this.store.select(selectSelectedPathSegment);
  }

  setRecentPathSelectedSegment(segment) {
    this.store.dispatch(setRecentPathSelectedSegment({ segment }));
  }

  // ===== Store Selectors =====
  getAllFilters(): Observable<FiltersState> {
    return this.store.select(selectAllFilters);
  }

  getCurrentFilter(): Observable<Filter> {
    return this.store.select(selectCurrentFilter);
  }

  getAssetCount(): Observable<number> {
    return this.store.select(selectAssetCount);
  }

  getAllAssets(): Observable<ViewableAsset[]> {
    return this.store.select(selectAllAssets);
  }

  getAllVehicles(): Observable<Map<string, VehicleListItem>> {
    return this.store.select(selectAllVehicles);
  }

  getAssetsLoadState(): Observable<ResourceLoadState> {
    return this.store.select(selectAssetsLoadState);
  }

  getVehiclesLoadState(): Observable<ResourceLoadState> {
    return this.store.select(selectVehiclesLoadState);
  }

  getSearchLoadState(): Observable<ResourceLoadState> {
    return this.store.select(selectPlacesLoadState);
  }

  setAutoSearchResult(params: AutoSuggestRequest) {
    this.store.dispatch(loadPlacesResult({ params }));
  }

  clearSearchResult() {
    this.store.dispatch(clearPlacesResult());
  }

  getAutoSearchResult(): Observable<AutoSuggestionResponse> {
    return this.store.select(selectPlacesResults);
  }

  getCompaniesLoadState(): Observable<ResourceLoadState> {
    return this.store.select(selectCompaniesLoadingState);
  }

  getNearbyAssets(): Observable<ViewableAsset[]> {
    return this.store.select(selectNearbyAssets);
  }

  getNearbyAssetsLoadState(): Observable<ResourceLoadState> {
    return this.store.select(selectNearbyAssetsLoadState);
  }

  getNearbyAssetsEnabled(): Observable<boolean> {
    return this.store.select(selectNearbyAssetsEnabled);
  }

  getCompaniesSearchFilterOptions(): Observable<DropdownOption[]> {
    return this.store.select(selectCompaniesSearchFilterOptions);
  }

  getDivisionsSearchFilterOptions(): Observable<DropdownOption[]> {
    return this.store.select(selectDivisionsSearchFilterOptions);
  }

  getFilterChipLabels() {
    return this.store.select(selectFilterChipLabels);
  }

  getIsAssetSelected() {
    return this.store.select(selectIsAssetSelected);
  }

  // getLocationsOptionList(): Observable<DropdownOption[]> {
  //   return this.store.select(selectLocationsSearchFilterOptions);
  // }

  getReadCompaniesPerm() {
    return this.store.select(selectReadCompaniesPerm);
  }

  getUserPermissions() {
    return this.store.select(selectAllPermissions);
  }

  getSelectedAsset(): Observable<ViewableAsset> {
    return this.store.select(selectChosenAsset);
  }

  getSelectViewContext() {
    return this.store.select(selectViewContext);
  }

  getSelectedViewPaneOpen() {
    return this.store.select(selectViewPaneOpen);
  }

  getSelectedViewSubContext() {
    return this.store.select(selectViewSubContext);
  }

  setLimitedUserCompanyAndDivisions(params: { companyId: string; divisions: string[] }) {
    return this.store.dispatch(setLimitedUserCompanyAndDivisions(params));
  }

  getShowFilterChipsForLimitedUser() {
    return this.store.select(selectShowFilterChipsForLimitedUser);
  }

  getLocationsForLimitedUser(locations: string[]) {
    this.store.dispatch(setLocationsForLimitedUser({ locations }));
  }

  // clearLocations() {
  //   this.store.dispatch(clearSearchFiltersLocations());
  // }

  // currently used in app component to fetch assets on company switch for Zonar/multicompany user
  setAssets(assetsParams: AssetsParams) {
    this.store.dispatch(loadAssets({ assetsParams }));
  }

  forceUpdateAssets() {
    this.store.dispatch(forceUpdateAssets());
  }

  loadVehicles(accountId: string) {
    this.store.dispatch(loadVehicles({ accountId }));
  }

  // when user switches between companies, dispatches current company to state
  setCurrentCompany(company: EntityResource) {
    this.store.dispatch(setCurrentCompany({ company }));
  }

  getAssetParams() {
    return this.store.select(selectAssetParams);
  }

  getReverseGeocode() {
    return this.store.select(selectReverseGeocode);
  }
  getReverseGeocodeStatus() {
    return this.store.select(selectReverseGeocodeStatus);
  }
  getReverseGeocodeForPoint(lat, lon) {
    return this.geocode.getReverseGeocode(lat, lon);
  }

  getAssetsError(): Observable<any> {
    return this.store.select(selectAssetsLoadError);
  }

  setAssetsError(assetsLoadError = true as any) {
    this.store.dispatch(loadAssetsFailure({ assetsLoadError }));
  }

  setCompaniesError(error?) {
    let errorToSet;
    if (!error) {
      errorToSet = {
        error: {
          error: {
            message: 'default companies error message sent manually'
          }
        }
      };
    } else {
      errorToSet = error;
    }
    this.store.dispatch(getCompaniesFailure(errorToSet));
  }

  toggleIo(i: number, isDisplayed: boolean): void {
    this.leafletService.toggleIo(i, isDisplayed);

    this.getIsMobile()
      .pipe(first())
      .subscribe(isMobile => {
        if (isMobile && isDisplayed) {
          this.setViewContext(ViewContext.SPLIT_VIEW);
        }
      });
  }

  setSelectedAssetById(assetId: string) {
    this.store.dispatch(loadSelectedAssetById({ assetId }));
  }

  setSelectedAssetSuccess(asset: Asset) {
    this.store.dispatch(loadSelectedAssetSuccess({ asset }));
  }

  setSelectedAssetError(failure: any) {
    this.store.dispatch(loadSelectedAssetFailure({ failure }));
  }

  getSelectedAssetLoadState() {
    return this.store.select(selectSelectedAssetLoadingState);
  }

  clearSelectedAsset() {
    this.leafletService.clearSelectedAsset();
    return this.store.dispatch(clearSelectedAsset());
  }

  filterAssets(
    assets: ViewableAsset[],
    driversMap: Map<string, DriverProfle>,
    { powerOn, searchTerms }: { powerOn: boolean; searchTerms: string[] }
  ) {
    const uniqueIds = new Set();
    return assets?.filter(asset => {
      if (powerOn && !asset.powerOn) return false;
      if (searchTerms.length === 0) return true;
      let fullName = '';
      if (asset.driverProfileId) {
        const driver = driversMap.get(asset.driverProfileId);
        if (driver) {
          fullName = `${driver.legacyFirstName || ''} ${driver.legacyLastName || ''}`.toLowerCase();
        }
      }
      const namesMatch = searchTerms.every((serachTerm: string) => {
        const lowerSearchTerm = serachTerm?.toLowerCase();
        return (
          asset.assetName?.toLowerCase()?.includes(lowerSearchTerm) ||
          // asset?.driverFirstName?.toLowerCase()?.includes(lowerSearchTerm) ||
          // asset?.driverLastName?.toLowerCase()?.includes(lowerSearchTerm) ||
          fullName.includes(lowerSearchTerm) ||
          asset.assetDisplayName?.toLowerCase()?.includes(lowerSearchTerm)
        );
      });
      const isUnique = !uniqueIds.has(asset.assetId);
      if (namesMatch && isUnique) {
        uniqueIds.add(asset.assetId);
        return true;
      }
      return false;
    });
  }
}
